/**
 * Number of messages to fetch per page
 */
export const MESSAGE_FETCH_LIMIT = 50;

/**
 * Channel URL format: workplaceId_workerId
 */
export function createChannelUrl(workplaceId: string, workerId: string): string {
  return `${workplaceId}_${workerId}`;
}

/**
 * Parse channel URL to extract workplace and worker IDs
 */
export function parseChannelUrl(channelUrl: string): {
  workplaceId: string;
  workerId: string;
} | null {
  const parts = channelUrl.split("_");
  if (parts.length !== 2) {
    return null;
  }
  return {
    workplaceId: parts[0],
    workerId: parts[1],
  };
}

/**
 * Message auto-refresh interval in milliseconds
 */
export const MESSAGE_REFRESH_INTERVAL = 30_000; // 30 seconds

/**
 * Maximum file upload size in bytes (10MB)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Supported file types for upload
 */
export const SUPPORTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;

/**
 * Analytics event names for chat interactions
 */
export const CHAT_ANALYTICS_EVENTS = {
  CHAT_OPENED: "Workplace Web: Chat opened",
  CHAT_MESSAGE_SENT: "Workplace Web: Chat message_sent",
  CHAT_PAGE_VIEWED: "Workplace Web: Chat page viewed",
  CHAT_CONVERSATION_SELECTED: "Workplace Web: Chat conversation selected",
  CHAT_FILTER_CHANGED: "Workplace Web: Chat filter changed",
} as const;

/**
 * Custom message types from Sendbird.
 * Some map to existing MessageCustomTypes in legacy code, others are new.
 */
export const MESSAGE_CUSTOM_TYPES = {
  // === Hidden from workplace view ===
  INPUT_ETA: "INPUT_ETA",
  INPUT_ETA_SUBMITTED: "INPUT_ETA_SUBMITTED",
  SHIFT_REVIEW: "SHIFT_REVIEW",
  SHIFT_ACTION: "SHIFT_ACTION",
  SHIFT_TIME_PROPOSAL_UPDATED: "SHIFT_TIME_PROPOSAL_UPDATED",
  SHIFT_TIME_PROPOSAL_REPLIED: "SHIFT_TIME_PROPOSAL_REPLIED",
  SHIFT_INVITE_RESPONSE: "SHIFT_INVITE_RESPONSE",
  FCM_APPROVAL: "FCM_APPROVAL",
  FCM_REJECT: "FCM_REJECT",

  // === Visible as system events ===
  SHIFT_INVITE_REQUEST: "SHIFT_INVITE_REQUEST",
  GROUP_SHIFT_INVITE_REQUEST: "GROUP_SHIFT_INVITE_REQUEST",
  SHIFT_BOOKED: "SHIFT_BOOKED",
  SHIFT_TIME_PROPOSAL_CREATED: "SHIFT_TIME_PROPOSAL_CREATED",
  SHIFT_INVITE_REMINDER: "SHIFT_INVITE_REMINDER",
  WORKER_ACCEPT_SHIFT_INVITE: "WORKER_ACCEPT_SHIFT_INVITE",
  WORKER_REJECT_SHIFT_INVITE: "WORKER_REJECT_SHIFT_INVITE",
  WORKER_MARK_SHIFT_INTEREST: "WORKER_MARK_SHIFT_INTEREST",
  WORKER_UPDATED_ETA: "WORKER_UPDATED_ETA",
  WORKER_CHECKED_IN: "WORKER_CHECKED_IN",
  WORKER_CHECKED_OUT: "WORKER_CHECKED_OUT",
  SHIFT_CANCELLED: "SHIFT_CANCELLED",

  // === Placement messages (rich card UI) ===
  PLACEMENT_APPLICATION: "PLACEMENT_APPLICATION",
  PLACEMENT_APPLICATION_INVITE: "PLACEMENT_APPLICATION_INVITE",
  PLACEMENT_APPLICATION_AUTO_INTERVIEW_INVITE: "PLACEMENT_APPLICATION_AUTO_INTERVIEW_INVITE",
  PLACEMENT_APPLICATION_SUGGEST_INTERVIEW: "PLACEMENT_APPLICATION_SUGGEST_INTERVIEW",
  PLACEMENT_APPLICATION_CHAT_INTERVIEW_SCHEDULED: "PLACEMENT_APPLICATION_CHAT_INTERVIEW_SCHEDULED",
  INTERVIEW_WORKER_CANCELLATION: "INTERVIEW_WORKER_CANCELLATION",

  // === Home Health messages (rich card UI) ===
  VISIT_INVITE_REQUEST: "VISIT_INVITE_REQUEST",

  // === Worker credential messages ===
  WORKER_CREDENTIAL_REQUEST: "WORKER_CREDENTIAL_REQUEST",
  WORKER_CREDENTIAL_PROVISION_REQUEST_COMPLETED: "WORKER_CREDENTIAL_PROVISION_REQUEST_COMPLETED",

  // === Facility Canceled Me (Sent Home) messages ===
  FCM_REQUEST: "FCM_REQUEST",

  // === Worker lock notification ===
  WORKER_LOCK_NOTIFICATION: "WORKER_LOCK_NOTIFICATION",
} as const;

export type MessageCustomType = (typeof MESSAGE_CUSTOM_TYPES)[keyof typeof MESSAGE_CUSTOM_TYPES];

/**
 * Message types that should be hidden from the workplace view.
 * These are internal system messages not meant for workplace users.
 */
export const HIDDEN_MESSAGE_TYPES: readonly MessageCustomType[] = [
  MESSAGE_CUSTOM_TYPES.INPUT_ETA,
  MESSAGE_CUSTOM_TYPES.INPUT_ETA_SUBMITTED,
  MESSAGE_CUSTOM_TYPES.SHIFT_REVIEW,
  MESSAGE_CUSTOM_TYPES.SHIFT_ACTION,
  MESSAGE_CUSTOM_TYPES.SHIFT_TIME_PROPOSAL_UPDATED,
  MESSAGE_CUSTOM_TYPES.SHIFT_TIME_PROPOSAL_REPLIED,
  MESSAGE_CUSTOM_TYPES.SHIFT_INVITE_RESPONSE,
  MESSAGE_CUSTOM_TYPES.FCM_APPROVAL,
  MESSAGE_CUSTOM_TYPES.FCM_REJECT,
] as const;

/**
 * Message types that should be displayed as centered system events.
 * These use Apple-like text styling with contextual colors.
 */
export const SYSTEM_EVENT_MESSAGE_TYPES: readonly MessageCustomType[] = [
  MESSAGE_CUSTOM_TYPES.SHIFT_INVITE_REMINDER,
  MESSAGE_CUSTOM_TYPES.SHIFT_BOOKED,
  MESSAGE_CUSTOM_TYPES.WORKER_ACCEPT_SHIFT_INVITE,
  MESSAGE_CUSTOM_TYPES.WORKER_REJECT_SHIFT_INVITE,
  MESSAGE_CUSTOM_TYPES.WORKER_MARK_SHIFT_INTEREST,
  MESSAGE_CUSTOM_TYPES.WORKER_UPDATED_ETA,
  MESSAGE_CUSTOM_TYPES.WORKER_CHECKED_IN,
  MESSAGE_CUSTOM_TYPES.WORKER_CHECKED_OUT,
  MESSAGE_CUSTOM_TYPES.SHIFT_CANCELLED,
] as const;

/**
 * Shift invite message types that need rich card UI with actions.
 */
export const SHIFT_INVITE_MESSAGE_TYPES: readonly MessageCustomType[] = [
  MESSAGE_CUSTOM_TYPES.SHIFT_INVITE_REQUEST,
  MESSAGE_CUSTOM_TYPES.GROUP_SHIFT_INVITE_REQUEST,
] as const;

/**
 * Placement-related message types that need rich card UI with actions.
 */
export const PLACEMENT_MESSAGE_TYPES: readonly MessageCustomType[] = [
  MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION,
  MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION_INVITE,
  MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION_AUTO_INTERVIEW_INVITE,
  MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION_SUGGEST_INTERVIEW,
  MESSAGE_CUSTOM_TYPES.PLACEMENT_APPLICATION_CHAT_INTERVIEW_SCHEDULED,
  MESSAGE_CUSTOM_TYPES.INTERVIEW_WORKER_CANCELLATION,
] as const;

/**
 * Home Health visit invite message types that need rich card UI.
 * Only shown when useIsHomeHealthEnabled() returns true.
 */
export const VISIT_INVITE_MESSAGE_TYPES: readonly MessageCustomType[] = [
  MESSAGE_CUSTOM_TYPES.VISIT_INVITE_REQUEST,
] as const;

/**
 * Worker credential message types that show credential management UI.
 * Only shown when charting credential integration is enabled.
 */
export const WORKER_CREDENTIAL_MESSAGE_TYPES: readonly MessageCustomType[] = [
  MESSAGE_CUSTOM_TYPES.WORKER_CREDENTIAL_REQUEST,
  MESSAGE_CUSTOM_TYPES.WORKER_CREDENTIAL_PROVISION_REQUEST_COMPLETED,
] as const;

/**
 * Facility Canceled Me (Sent Home) request message types that need rich card UI.
 * Displays sent home request details with accept/decline actions.
 */
export const FCM_REQUEST_MESSAGE_TYPES: readonly MessageCustomType[] = [
  MESSAGE_CUSTOM_TYPES.FCM_REQUEST,
] as const;

/**
 * Shift time proposal message types that need rich card UI.
 * Shows proposed and original shift times with status chip.
 */
export const SHIFT_TIME_PROPOSAL_MESSAGE_TYPES: readonly MessageCustomType[] = [
  MESSAGE_CUSTOM_TYPES.SHIFT_TIME_PROPOSAL_CREATED,
] as const;
